import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/services/client'
import { useTeam } from '@/shared/context/TeamContext'
import type {
  Laboratorio, LaboratorioCreate, LaboratorioUpdate,
  Marca, MarcaCreate, MarcaUpdate,
  ConfigCampania, ConfigCampaniaCreate, ConfigCampaniaUpdate,
  ConfigGeneral, ConfigGeneralCreate, ConfigGeneralUpdate, ConfigGeneralEntity,
  Benchmark, BenchmarkCreate, BenchmarkUpdate,
  VariacionBenchmark, VariacionBenchmarkCreate, VariacionBenchmarkUpdate,
  GrupoOption,
} from '@/modules/multichannel/types/configuracion'

// ============================================================================
// LABORATORIOS
// ============================================================================

export function useLaboratorios(filters?: { activo?: boolean }) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['config-laboratorios', selectedArea?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.activo !== undefined) params.append('activo', String(filters.activo))
      const response = await apiClient.get<Laboratorio[]>(
        `/multichannel/marcas-principales/?${params.toString()}`
      )
      return response.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateLaboratorio() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: LaboratorioCreate) => {
      const response = await apiClient.post<Laboratorio>('/multichannel/marcas-principales/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-laboratorios'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useUpdateLaboratorio() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: LaboratorioUpdate }) => {
      const response = await apiClient.put<Laboratorio>(`/multichannel/marcas-principales/${id}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-laboratorios'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useDeleteLaboratorio() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/multichannel/marcas-principales/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-laboratorios'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

// ============================================================================
// MARCAS
// ============================================================================

export function useMarcas(filters?: { marca_principal_id?: number; activo?: boolean }) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['config-marcas', selectedArea?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.marca_principal_id) params.append('marca_principal_id', String(filters.marca_principal_id))
      if (filters?.activo !== undefined) params.append('activo', String(filters.activo))
      const response = await apiClient.get<Marca[]>(
        `/multichannel/marcas-secundarias/?${params.toString()}`
      )
      return response.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateMarca() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: MarcaCreate) => {
      const response = await apiClient.post<Marca>('/multichannel/marcas-secundarias/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-marcas'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useUpdateMarca() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: MarcaUpdate }) => {
      const response = await apiClient.put<Marca>(`/multichannel/marcas-secundarias/${id}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-marcas'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useDeleteMarca() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/multichannel/marcas-secundarias/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-marcas'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

// ============================================================================
// CONFIGURACIONES DE CAMPAÑA
// ============================================================================

export function useConfiguraciones(filters?: { marca_secundaria_id?: number; activo?: boolean }) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['config-configuraciones', selectedArea?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.marca_secundaria_id) params.append('marca_secundaria_id', String(filters.marca_secundaria_id))
      if (filters?.activo !== undefined) params.append('activo', String(filters.activo))
      const response = await apiClient.get<ConfigCampania[]>(
        `/multichannel/configuraciones/?${params.toString()}`
      )
      return response.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateConfiguracion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: ConfigCampaniaCreate) => {
      const response = await apiClient.post<ConfigCampania>('/multichannel/configuraciones/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-configuraciones'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useUpdateConfiguracion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ConfigCampaniaUpdate }) => {
      const response = await apiClient.put<ConfigCampania>(`/multichannel/configuraciones/${id}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-configuraciones'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useDeleteConfiguracion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/multichannel/configuraciones/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-configuraciones'] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

// ============================================================================
// CONFIGURACIÓN GENERAL (genérico para 7 entidades)
// ============================================================================

export function useConfigGeneral(entity: ConfigGeneralEntity, filters?: { activo?: boolean }) {
  return useQuery({
    queryKey: ['config-general', entity, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.activo !== undefined) params.append('activo', String(filters.activo))
      const response = await apiClient.get<ConfigGeneral[]>(
        `/multichannel/${entity}/?${params.toString()}`
      )
      return response.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateConfigGeneral(entity: ConfigGeneralEntity) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: ConfigGeneralCreate) => {
      const response = await apiClient.post<ConfigGeneral>(`/multichannel/${entity}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-general', entity] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useUpdateConfigGeneral(entity: ConfigGeneralEntity) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ConfigGeneralUpdate }) => {
      const response = await apiClient.put<ConfigGeneral>(`/multichannel/${entity}/${id}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-general', entity] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

export function useDeleteConfigGeneral(entity: ConfigGeneralEntity) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/multichannel/${entity}/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-general', entity] })
      queryClient.invalidateQueries({ queryKey: ['multichannel-filters'] })
    },
  })
}

// ============================================================================
// BENCHMARKS
// ============================================================================

export function useBenchmarks(filters?: { activo?: boolean }) {
  return useQuery({
    queryKey: ['config-benchmarks', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.activo !== undefined) params.append('activo', String(filters.activo))
      const response = await apiClient.get<Benchmark[]>(
        `/multichannel/benchmarks/?${params.toString()}`
      )
      return response.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateBenchmark() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: BenchmarkCreate) => {
      const response = await apiClient.post<Benchmark>('/multichannel/benchmarks/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-benchmarks'] })
    },
  })
}

export function useUpdateBenchmark() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: BenchmarkUpdate }) => {
      const response = await apiClient.put<Benchmark>(`/multichannel/benchmarks/${id}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-benchmarks'] })
    },
  })
}

export function useDeleteBenchmark() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/multichannel/benchmarks/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-benchmarks'] })
    },
  })
}

// ============================================================================
// VARIACIONES BENCHMARK
// ============================================================================

export function useVariaciones(filters?: { activo?: boolean }) {
  return useQuery({
    queryKey: ['config-variaciones', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.activo !== undefined) params.append('activo', String(filters.activo))
      const response = await apiClient.get<VariacionBenchmark[]>(
        `/multichannel/variaciones-benchmark/?${params.toString()}`
      )
      return response.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateVariacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: VariacionBenchmarkCreate) => {
      const response = await apiClient.post<VariacionBenchmark>('/multichannel/variaciones-benchmark/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-variaciones'] })
    },
  })
}

export function useUpdateVariacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: VariacionBenchmarkUpdate }) => {
      const response = await apiClient.put<VariacionBenchmark>(`/multichannel/variaciones-benchmark/${id}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-variaciones'] })
    },
  })
}

export function useDeleteVariacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/multichannel/variaciones-benchmark/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-variaciones'] })
    },
  })
}

// ============================================================================
// GRUPOS (para select en LaboratorioFormDialog)
// ============================================================================

export function useGrupos() {
  return useQuery({
    queryKey: ['config-grupos'],
    queryFn: async () => {
      const response = await apiClient.get<GrupoOption[]>('/group-extensions/laboratorios/')
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })
}
